// Slick Slider inspired with
/*!
 * Copyright (c) 2024 by Pedro Castro (https://codepen.io/aspeddro/pen/zqyJBr)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software 
 * and associated documentation files (the "Software"), to deal in the Software without restriction, 
 * including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, 
 * and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, 
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies 
 * or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, 
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
 * PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE 
 * FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, 
 * ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *  * Modified by: Mana Themes
 * Changes:
 * - The JavaScript has been extensively rewritten to enhance functionality and adapt it for the theme's specific requirements.
 * - The Liquid files have been customized to align with the theme's design and layout needs.
 */

class SlickSlider extends HTMLElement {
    constructor() {
      super();
      this.current = 0;
      this.autoUpdate = false;
      this.timeTrans = 4000;
      this.slides = null;
    }
  
    connectedCallback() {
      this.initSlider();
    }
  
  initSlider() {
    const slickMain = this.closest(".slick-main");
    if (slickMain) {
      const enableAutoplay = slickMain.getAttribute("data-enable-autoplay");
      const autoplaySpeed = slickMain.getAttribute("data-autoplay-speed");
      this.autoUpdate = enableAutoplay === "true";
      this.timeTrans = autoplaySpeed ? parseInt(autoplaySpeed) * 1000 : 4000;
    }
  
    this.slides = this.querySelectorAll("li");
  
    if (this.autoUpdateInterval) {
      clearInterval(this.autoUpdateInterval);
      this.autoUpdateInterval = null;
    }
  
    const existingNav = this.querySelector(".nav_arrows");
    if (existingNav) existingNav.remove();
  
    if (this.slides.length > 1) {
      const customBtn = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.94757 5.96967C9.65467 5.67678 9.1798 5.67678 8.88691 5.96967L3.52833 11.3282C3.15733 11.6992 3.15733 12.3008 3.52833 12.6718L8.88691 18.0303C9.1798 18.3232 9.65467 18.3232 9.94757 18.0303C10.2405 17.7374 10.2405 17.2626 9.94757 16.9697L5.72789 12.75H19.9172C20.3314 12.75 20.6672 12.4142 20.6672 12C20.6672 11.5858 20.3314 11.25 19.9172 11.25H5.72789L9.94757 7.03033C10.2405 6.73744 10.2405 6.26256 9.94757 5.96967Z" fill="#0F1D24"/>
          </svg>

      `

      const nav = document.createElement("slick-nav");
      nav.className = "nav_arrows";
  
      const prevBtn = document.createElement("button");
      prevBtn.className = "prev";
      prevBtn.setAttribute("aria-label", "Prev");
      prevBtn.innerHTML = customBtn;
  
      const nextBtn = document.createElement("button");
      nextBtn.className = "next";
      nextBtn.setAttribute("aria-label", "Next");
      nextBtn.innerHTML = customBtn; 
  
      const counter = document.createElement("div");
      counter.className = "slick-counter";
      counter.innerHTML = `<span>1</span><span>${this.slides.length}</span>`;
  
      nav.appendChild(prevBtn);
      nav.appendChild(counter);
      nav.appendChild(nextBtn);
      this.appendChild(nav);
  
      this.slides[this.current].classList.add("current");
      this.slides[this.slides.length - 1].classList.add("prev_slide");
  
      prevBtn.addEventListener("click", () => this.navigate("left"));
      nextBtn.addEventListener("click", () => this.navigate("right"));
  
      if (this.autoUpdate) {
        this.autoUpdateInterval = setInterval(() => {
          this.navigate("right");
        }, this.timeTrans);
      }
  
      this.addEventListener("mouseenter", () => {
        if (this.autoUpdateInterval) clearInterval(this.autoUpdateInterval);
      });
  
      this.addEventListener("mouseleave", () => {
        if (this.autoUpdate && !this.autoUpdateInterval) {
          this.autoUpdateInterval = setInterval(() => {
            this.navigate("right");
          }, this.timeTrans);
        }
      });
  
      document.addEventListener("keydown", (ev) => {
        if (ev.key === "ArrowLeft") this.navigate("left");
        if (ev.key === "ArrowRight") this.navigate("right");
      });
  
      this.addEventListener("touchstart", (ev) => this.handleTouchStart(ev), false);
      this.addEventListener("touchmove", (ev) => this.handleTouchMove(ev), false);
    }

    window.addEventListener('resize', () => this.updateHeight());
    this.updateHeight();
    
  }

  updateHeight() {
    if (window.innerWidth < 768) {
      let maxHeight = 0;

      this.slides.forEach(item => {
        const slickImage = item.querySelector('.slick-image');
        const slickContent = item.querySelector('.slick-content');
        
        const h = slickImage.offsetHeight + slickContent.offsetHeight;
        if (h > maxHeight) maxHeight = h;
      });

      this.slides.forEach(item => {
        this.style.height = maxHeight + 'px';
        item.style.marginBottom = 52 + 'px';
      });
    } else {
      this.slides.forEach(item => {
        this.style.removeProperty('height');
        item.style.removeProperty('margin-bottom');
      });
    }

  }
  
    navigate(direction) {
      this.slides.forEach((slide) => {
        slide.classList.remove("current", "prev_slide");
      });
  
      if (direction === "right") {
        this.current =
          this.current < this.slides.length - 1 ? this.current + 1 : 0;
      } else {
        this.current =
          this.current > 0 ? this.current - 1 : this.slides.length - 1;
      }

      
      const nextCurrent =
      this.current < this.slides.length - 1 ? this.current + 1 : 0;
      const prevCurrent =
      this.current > 0 ? this.current - 1 : this.slides.length - 1;
      
      this.slides[this.current].classList.add("current");
      this.slides[prevCurrent].classList.add("prev_slide");
      const manualCounter = this.querySelector('.manual-slick-counter .current');
      manualCounter.textContent = this.current + 1;
      this.querySelector(".slick-counter span:first-child").textContent =
      this.current + 1;
      this.updateHeight();
       
    }
    handleTouchStart(evt) {
      this.xDown = evt.touches[0].clientX;
      this.yDown = evt.touches[0].clientY;
    }
  
    handleTouchMove(evt) {
      if (!this.xDown || !this.yDown) return;
  
      const xUp = evt.touches[0].clientX;
      const yUp = evt.touches[0].clientY;
  
      const xDiff = this.xDown - xUp;
      const yDiff = this.yDown - yUp;
  
      if (Math.abs(xDiff) > Math.abs(yDiff)) {
        if (xDiff > 0) this.navigate("right");
        else this.navigate("left");
      }
  
      this.xDown = null;
      this.yDown = null;
    }
    disconnectedCallback() {
    if (this.autoUpdateInterval) {
      clearInterval(this.autoUpdateInterval);
    }
   }
  }
  
  customElements.define("slick-slider", SlickSlider);

  document.addEventListener('shopify:section:load', (event) => {
  const sliders = event.target.querySelectorAll('slick-slider');
  sliders.forEach(slider => slider.initSlider());
});